import type { ElementType, ReactNode } from "react";
import { twJoin } from "tailwind-merge";

import type { DesignSystemAs, DesignSystemElementProps, DesignSystemRef } from "./contracts/dom";
import type { Space } from "./foundations/space";

export type BoxPadding = Space | { x?: Space; y?: Space; top?: Space; bottom?: Space; start?: Space; end?: Space };

const BOX_PADDING_CLASSES: Record<Space, string> = {
  xs: "p-space-xs",
  sm: "p-space-sm",
  md: "p-space-md",
  lg: "p-space-lg",
  xl: "p-space-xl",
};
const BOX_PADDING_X_CLASSES: Record<Space, string> = {
  xs: "px-space-xs",
  sm: "px-space-sm",
  md: "px-space-md",
  lg: "px-space-lg",
  xl: "px-space-xl",
};
const BOX_PADDING_Y_CLASSES: Record<Space, string> = {
  xs: "py-space-xs",
  sm: "py-space-sm",
  md: "py-space-md",
  lg: "py-space-lg",
  xl: "py-space-xl",
};
const BOX_PADDING_TOP_CLASSES: Record<Space, string> = {
  xs: "pt-space-xs",
  sm: "pt-space-sm",
  md: "pt-space-md",
  lg: "pt-space-lg",
  xl: "pt-space-xl",
};
const BOX_PADDING_BOTTOM_CLASSES: Record<Space, string> = {
  xs: "pb-space-xs",
  sm: "pb-space-sm",
  md: "pb-space-md",
  lg: "pb-space-lg",
  xl: "pb-space-xl",
};
const BOX_PADDING_START_CLASSES: Record<Space, string> = {
  xs: "ps-space-xs",
  sm: "ps-space-sm",
  md: "ps-space-md",
  lg: "ps-space-lg",
  xl: "ps-space-xl",
};
const BOX_PADDING_END_CLASSES: Record<Space, string> = {
  xs: "pe-space-xs",
  sm: "pe-space-sm",
  md: "pe-space-md",
  lg: "pe-space-lg",
  xl: "pe-space-xl",
};

function resolveBoxPadding(padding: BoxPadding | undefined): string | undefined {
  if (!padding) {
    return undefined;
  }
  if (typeof padding === "string") {
    return BOX_PADDING_CLASSES[padding];
  }

  return twJoin(
    padding.x && BOX_PADDING_X_CLASSES[padding.x],
    padding.y && BOX_PADDING_Y_CLASSES[padding.y],
    padding.top && BOX_PADDING_TOP_CLASSES[padding.top],
    padding.bottom && BOX_PADDING_BOTTOM_CLASSES[padding.bottom],
    padding.start && BOX_PADDING_START_CLASSES[padding.start],
    padding.end && BOX_PADDING_END_CLASSES[padding.end],
  );
}

const BOX_BACKGROUND_CLASSES = {
  default: "bg-background-default",
  secondary: "bg-background-secondary",
  tertiary: "bg-background-tertiary",
  brand: "bg-background-brand",
  danger: "bg-background-danger",
  success: "bg-background-success",
  warning: "bg-background-warning",
  info: "bg-background-info-secondary",
} as const;

const BOX_BORDER_CLASSES = {
  default: "border-border-default",
  secondary: "border-border-secondary",
  tertiary: "border-border-tertiary",
  brand: "border-border-brand",
  danger: "border-border-danger",
  success: "border-border-success",
  warning: "border-border-warning",
  info: "border-border-info",
} as const;

const BOX_BORDER_WIDTH_CLASSES = {
  default: "border",
  hairline: "border-hairline",
} as const;

const BOX_RADIUS_CLASSES = {
  xs: "rounded-xs",
  sm: "rounded-sm",
  md: "rounded-md",
  lg: "rounded-lg",
  xl: "rounded-xl",
  full: "rounded-full",
} as const;

const BOX_ELEVATION_CLASSES = {
  surface: "shadow-surface",
  raised: "shadow-raised",
  overlay: "shadow-overlay",
} as const;

type BoxElement = "div" | "section" | "article" | "aside" | "header" | "footer";

export type BoxStyleProps = {
  padding?: BoxPadding;
  background?: keyof typeof BOX_BACKGROUND_CLASSES;
  border?: keyof typeof BOX_BORDER_CLASSES;
  borderWidth?: keyof typeof BOX_BORDER_WIDTH_CLASSES;
  radius?: keyof typeof BOX_RADIUS_CLASSES;
  elevation?: keyof typeof BOX_ELEVATION_CLASSES;
};

export type BoxProps<TElement extends BoxElement = "div"> = {
  children?: ReactNode;
} & BoxStyleProps &
  DesignSystemAs<TElement> &
  DesignSystemElementProps<TElement> &
  DesignSystemRef<HTMLElementTagNameMap[TElement]>;

/** Token-backed surface styling for elements that must own their DOM and interaction behavior. */
export function boxVariants({
  padding,
  background,
  border,
  borderWidth = "default",
  radius,
  elevation,
}: BoxStyleProps): string {
  return twJoin(
    resolveBoxPadding(padding),
    background && BOX_BACKGROUND_CLASSES[background],
    border && BOX_BORDER_WIDTH_CLASSES[borderWidth],
    border && BOX_BORDER_CLASSES[border],
    radius && BOX_RADIUS_CLASSES[radius],
    elevation && BOX_ELEVATION_CLASSES[elevation],
  );
}

/** Paint and padding containment. Child layout belongs to Stack, Inline, Columns, or Tiles. */
export function Box<TElement extends BoxElement = "div">({
  as,
  background,
  border,
  borderWidth,
  children,
  elevation,
  padding,
  radius,
  ref,
  ...nativeProps
}: BoxProps<TElement>): ReactNode {
  const Element = (as ?? "div") as ElementType;

  return (
    <Element
      {...nativeProps}
      ref={ref}
      className={boxVariants({ padding, background, border, borderWidth, radius, elevation })}
    >
      {children}
    </Element>
  );
}
